<!--
  @name: 渠道日志
  @author: claude
  @time: 2024-01-20
-->
<template>
	<basic-container>
		<avue-crud
			v-model="formData"
			:page.sync="page"
			v-bind="bindVal"
			v-on="onEvent"
			@on-load="getList"
			@refresh-change="refreshChange"
			@search-reset="searchChange"
			@search-change="searchChange"></avue-crud>
	</basic-container>
</template>

<script>
import {mapGetters} from 'vuex'
import {listAddOption, listAddOptionPrivate} from '@/const/crud/channel/channelOption'
import {getSettlementRuleBasicList, getSettlementRuleAwardList} from '@/api/insurance/settlement'
import {searchWeworkGroup} from '@/api/team/place'

const option = {
	name: 'channelLog',
	optionPath: 'team/channelLog',
	optionNameList: ['channelLogOption'],
	apiPath: 'product/productManage',
	apiNameList: ['getChannelLog'],
	apiNameAddList: [''],
	apiNameUpdateList: [''],
	apiNameDelList: [''],
	searchData: [],
	rowKey: 'id',
}

export default window.$baseCrud(
	{
		props: {
			channelId: {
				type: [String, Number],
				required: true,
			},
		},
		async created() {
			try {
				// 获取字段中文映射和字段类型配置
				this.fieldLabelMap = {}
				this.fieldTypeMap = {}
				const columns = listAddOption.column || []

				// 获取结算方案字典数据，增加超时和重试机制
				let [basicData, awardData] = await Promise.all([
					this.safeApiCall(() => getSettlementRuleBasicList(), '获取基础结算方案失败'),
					this.safeApiCall(() => getSettlementRuleAwardList(), '获取奖励结算方案失败')
				])

				let tempBasicData = (basicData && basicData.data && basicData.data.data) || []
				let tempAwardData = (awardData && awardData.data && awardData.data.data) || []

				// 遍历 listAddOption 中的配置
				columns.forEach((item) => {
					if (item.prop) {
						// 保存字段标签映射
						if (item.label) {
							this.fieldLabelMap[item.prop] = item.label
						}

						// 特殊处理结算方案字典数据
						if (item.prop === 'ruleBasicId') {
							item.type = 'select'
							item.dicData = tempBasicData
							item.props = {
								label: 'name',
								value: 'id',
							}
						} else if (item.prop === 'ruleAwardIds') {
							item.type = 'select'
							item.dicData = tempAwardData
							item.props = {
								label: 'name',
								value: 'id',
							}
						}

						// 保存字段类型配置
						this.fieldTypeMap[item.prop] = {
							type: item.type,
							dicUrl: item.dicUrl,
							dicData: item.dicData,
							props: item.props || {
								label: 'label',
								value: 'value',
							},
						}

						// 如果是 select、switch、radio 类型且有 dicData，直接缓存
						if (
							(item.type === 'select' || item.type === 'switch' || item.type === 'radio') &&
							item.dicData &&
							Array.isArray(item.dicData)
						) {
							this.dictCache[item.prop] = item.dicData
						}
					}
				})

				this.isInitComplete = true
				console.log('渠道日志初始化完成')
			} catch (error) {
				console.error('渠道日志初始化失败:', error)
				this.isInitComplete = true // 即使失败也标记完成，避免阻塞
				this.$message.warning('部分数据初始化失败，可能影响显示效果')
			}
		},
		data() {
			return {
				fieldLabelMap: {},
				fieldTypeMap: {},
				// 缓存字典数据
				dictCache: {},
				// 初始化完成标志
				isInitComplete: false,
				// 企微沟通群数据缓存
				weworkGroupCache: new Map(),
			}
		},
		computed: {
			...mapGetters(['permissions']),
		},
		mounted() {},
		methods: {
			// 安全的API调用方法
			async safeApiCall(apiFunc, errorMsg, timeout = 10000) {
				try {
					const timeoutPromise = new Promise((_, reject) => {
						setTimeout(() => reject(new Error('请求超时')), timeout)
					})
					
					const result = await Promise.race([apiFunc(), timeoutPromise])
					return result
				} catch (error) {
					console.error(errorMsg, error)
					return null
				}
			},

			// 等待初始化完成
			async waitForInit() {
				if (this.isInitComplete) return
				
				return new Promise((resolve) => {
					const timer = setInterval(() => {
						if (this.isInitComplete) {
							clearInterval(timer)
							resolve()
						}
					}, 50)
					
					// 最多等待5秒
					setTimeout(() => {
						clearInterval(timer)
						resolve()
					}, 5000)
				})
			},

			// 获取字典数据
			async getDictData(field) {
				try {
					const fieldConfig = this.fieldTypeMap[field]
					if (
						!fieldConfig ||
						!fieldConfig.type ||
						!(fieldConfig.type === 'select' || fieldConfig.type === 'switch' || fieldConfig.type === 'radio' || fieldConfig.type === 'checkbox')
					) {
						return null
					}

					// 如果已经缓存,直接返回
					if (this.dictCache[field]) {
						return this.dictCache[field]
					}

					// 如果有dicData直接使用
					if (fieldConfig.dicData) {
						this.dictCache[field] = fieldConfig.dicData
						return fieldConfig.dicData
					}

					// 如果有dicUrl则请求数据
					if (fieldConfig.dicUrl) {
						const res = await this.safeApiCall(
							() => this.$axios.get(fieldConfig.dicUrl),
							`获取字典数据失败(${field})`
						)
						
						if (res && res.data && res.data.code === 0) {
							this.dictCache[field] = res.data.data
							return res.data.data
						}
					}

					return null
				} catch (error) {
					console.error(`获取字典数据异常(${field}):`, error)
					return null
				}
			},

			// 转换下拉框的值为中文
			async formatDictValue(field, value) {
				try {
					// 特殊处理企微沟通群
					if (field === 'weworkGroupId' && value) {
						return await this.formatWeworkGroup(value)
					}

					const dictData = await this.getDictData(field)
					if (!dictData) return value

					const fieldConfig = this.fieldTypeMap[field]
					const {label: labelKey, value: valueKey} = fieldConfig.props

					// 统一处理select、switch、radio、checkbox类型
					if (["select", "switch", "radio", "checkbox"].includes(fieldConfig.type)) {
						if (!dictData || !Array.isArray(dictData) || dictData.length === 0) {
							return value
						}
						// 多选（checkbox或select多选）
						if (fieldConfig.type === 'checkbox' || (fieldConfig.type === 'select' && fieldConfig.multiple)) {
							let values = []
							if (Array.isArray(value)) {
								values = value
							} else if (typeof value === 'string') {
								values = value.split(',')
							} else if (value !== undefined && value !== null) {
								values = [value]
							}
							const labels = values.map((v) => {
								const item = dictData.find((d) => String(d[valueKey]) === String(v))
								return item ? item[labelKey] : v
							})
							return labels.join('、')
						} else {
							// 单选
							const item = dictData.find((d) => String(d[valueKey]) === String(value))
							return item ? item[labelKey] : value
						}
					}

					const searchValue = fieldConfig.type === 'switch' ? parseInt(value) : value
					const dictItem = dictData.find((item) => item[valueKey] == searchValue)

					if (dictItem) {
						return `${dictItem[labelKey]}`
					}
					return value
				} catch (error) {
					console.error(`格式化字典值异常(${field}):`, error)
					return value
				}
			},

			// 格式化企微沟通群
			async formatWeworkGroup(value) {
				try {
					const ids = typeof value === 'string' ? value : String(value)
					
					// 检查缓存
					if (this.weworkGroupCache.has(ids)) {
						return this.weworkGroupCache.get(ids)
					}

					const res = await this.safeApiCall(
						() => searchWeworkGroup({ids}),
						'获取企微沟通群数据失败'
					)
					
					if (res && res.data && res.data.code === 0 && res.data.data) {
						const groups = res.data.data
						const result = groups.map(item => item.name).join('、')
						// 缓存结果
						this.weworkGroupCache.set(ids, result)
						return result
					}
					
					return value
				} catch (error) {
					console.error('获取企微沟通群数据失败:', error)
					return value
				}
			},

			listBefore() {},

			async dealResponseData(response) {
				try {
					// 等待初始化完成
					await this.waitForInit()

					let {data} = response.data
					let formatData = data

					if (data && data.length > 0) {
						// 处理每条数据
						const processPromises = formatData.map(async (item) => {
							try {
								const {property = {}} = item
								const entries = Object.entries(property)

								// 收集所有字段处理的 Promise
								const fieldPromises = entries.map(async ([key, value]) => {
									try {
										const fieldConfig = this.fieldTypeMap[key]
										const label = this.fieldLabelMap[key] || key
										const {oldValue, newValue} = value

										// 如果oldValue或newValue为空，则不展示
										if (
											(oldValue === undefined || oldValue === null || oldValue === '') &&
											(newValue === undefined || newValue === null || newValue === '')
										) {
											return null
										}

										// 特殊处理 settleAccounts 字段
										if (key === 'settleAccounts') {
											return await this.formatSettleAccounts(oldValue, newValue)
										}

										// 特殊处理企微沟通群字段
										if (key === 'weworkGroupId') {
											return await this.formatWeworkGroupField(label, oldValue, newValue)
										}

										// 如果字段配置存在且有类型定义
										if (
											fieldConfig &&
											fieldConfig.type &&
											(fieldConfig.type === 'select' || fieldConfig.type === 'switch' || fieldConfig.type === 'radio' || fieldConfig.type === 'checkbox')
										) {
											const [oldText, newText] = await Promise.all([
												oldValue !== undefined ? this.formatDictValue(key, oldValue) : Promise.resolve('空'),
												newValue !== undefined ? this.formatDictValue(key, newValue) : Promise.resolve('空'),
											])
											return `${label}: ${oldText} -> ${newText}`
										} else {
											// 非字典字段直接显示值
											if (oldValue !== undefined && newValue !== undefined) {
												return `${label}: ${oldValue} -> ${newValue}`
											} else if (oldValue !== undefined) {
												return `${label}: ${oldValue} -> 空`
											} else if (newValue !== undefined) {
												return `${label}: 空 -> ${newValue}`
											}
										}
									} catch (error) {
										console.error(`处理字段异常(${key}):`, error)
										return `${this.fieldLabelMap[key] || key}: 数据处理异常`
									}
								})

								// 等待所有字段处理完成
								const contentArray = await Promise.all(fieldPromises)
								item.content = contentArray.filter(Boolean).join('<br/>')
								
								// 如果所有字段都不展示，则整条日志不展示
								if (contentArray.filter(Boolean).length === 0) {
									return null
								}
								return item
							} catch (error) {
								console.error('处理单条数据异常:', error)
								// 返回原始数据，避免整条记录丢失
								item.content = '数据处理异常'
								return item
							}
						})

						// 等待所有数据处理完成
						const processed = await Promise.all(processPromises)
						formatData = processed.filter(Boolean)

						// 按commitId降序排序
						formatData.sort((a, b) => {
							return parseFloat(b.commitId) - parseFloat(a.commitId)
						})
					}

					this.listData = formatData
				} catch (error) {
					console.error('处理响应数据异常:', error)
					this.$message.error('数据处理失败，请重试')
					this.listData = []
				}
			},

			// 格式化结算账户字段
			async formatSettleAccounts(oldValue, newValue) {
				try {
					const oldAccounts = oldValue ? JSON.parse(oldValue) : []
					const newAccounts = newValue ? JSON.parse(newValue) : []

					const accountChanges = []
					// 获取 listAddOptionPrivate 的字段配置
					const privateColumns = listAddOptionPrivate.column || []

					// 比较新旧账户信息
					if (oldAccounts.length === 0 && newAccounts.length > 0) {
						// 新增结算账户
						for (const account of newAccounts) {
							const details = []
							for (const col of privateColumns) {
								if (account[col.prop] && col.prop !== 'del') {
									details.push(`${col.label}: ${account[col.prop]}`)
								}
							}
							accountChanges.push(`新增结算账户: ${details.join(', ')}`)
						}
					} else if (newAccounts.length === 0 && oldAccounts.length > 0) {
						// 删除结算账户
						for (const account of oldAccounts) {
							const details = []
							for (const col of privateColumns) {
								if (account[col.prop] && col.prop !== 'del') {
									details.push(`${col.label}: ${account[col.prop]}`)
								}
							}
							accountChanges.push(`删除结算账户: ${details.join(', ')}`)
						}
					} else {
						// 修改结算账户
						for (let i = 0; i < Math.max(oldAccounts.length, newAccounts.length); i++) {
							const oldAccount = oldAccounts[i] || {}
							const newAccount = newAccounts[i] || {}
							const changes = []

							for (const col of privateColumns) {
								if (col.prop !== 'del' && oldAccount[col.prop] !== newAccount[col.prop]) {
									changes.push(`${col.label}: ${oldAccount[col.prop] || '空'} -> ${newAccount[col.prop] || '空'}`)
								}
							}

							if (changes.length > 0) {
								accountChanges.push(`修改结算账户${i + 1}: ${changes.join(', ')}`)
							}
						}
					}

					return accountChanges.length > 0 ? accountChanges.join('<br/>') : null
				} catch (error) {
					console.error('格式化结算账户异常:', error)
					return '结算账户数据处理异常'
				}
			},

			// 格式化企微沟通群字段
			async formatWeworkGroupField(label, oldValue, newValue) {
				try {
					let oldText = '空', newText = '空'
					
					if (oldValue !== undefined) {
						oldText = await this.formatWeworkGroup(oldValue)
					}
					
					if (newValue !== undefined) {
						newText = await this.formatWeworkGroup(newValue)
					}
					
					return `${label}: ${oldText} -> ${newText}`
				} catch (error) {
					console.error('格式化企微沟通群字段异常:', error)
					// 降级处理
					if (oldValue !== undefined && newValue !== undefined) {
						return `${label}: ${oldValue} -> ${newValue}`
					} else if (oldValue !== undefined) {
						return `${label}: ${oldValue} -> 空`
					} else if (newValue !== undefined) {
						return `${label}: 空 -> ${newValue}`
					}
					return `${label}: 数据处理异常`
				}
			},

			listError() {},
			listFinally() {},

			addRequestData() {
				return {id: this.channelId, className: 'DistChannel'}
			},
			onCreated(editor) {},
		},
	},
	option,
)
</script>

<style lang="scss" scoped></style>
